import React, { useState, useEffect } from 'react';
import { useNewsfeedSource } from '@/contexts/NewsfeedSourceContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNewsfeedSourceCategories } from '@/hooks/useNewsfeedSourceCategories';
import { Switch } from '@/components/ui/switch';
import { Sparkles, FlaskConical, Loader2, Info } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ExtractedArticle {
  title: string;
  link: string;
  description?: string;
  image_url?: string;
  pub_date?: string;
}

export const GlobalNewsfeedSourceDialog = () => {
  const { isOpen, editingSource, closeSourceDialog } = useNewsfeedSource();
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();
  const { categories } = useNewsfeedSourceCategories();

  const [name, setName] = useState('');
  const [rssUrl, setRssUrl] = useState('');
  const [feedType, setFeedType] = useState<'rss' | 'atom' | 'custom_txt' | 'html'>('rss');
  const [categoryId, setCategoryId] = useState<string>('');
  const [maxArticles, setMaxArticles] = useState(5);
  const [fallbackImageFile, setFallbackImageFile] = useState<File | null>(null);
  const [currentFallbackImageUrl, setCurrentFallbackImageUrl] = useState<string | null>(null);

  // HTML scraping fields
  const [useAiExtraction, setUseAiExtraction] = useState(true);
  const [htmlArticleSelector, setHtmlArticleSelector] = useState('');
  const [htmlTitleSelector, setHtmlTitleSelector] = useState('');
  const [htmlLinkSelector, setHtmlLinkSelector] = useState('');
  const [htmlDescriptionSelector, setHtmlDescriptionSelector] = useState('');
  const [htmlImageSelector, setHtmlImageSelector] = useState('');
  const [htmlDateSelector, setHtmlDateSelector] = useState('');

  // Test extraction state
  const [testResults, setTestResults] = useState<ExtractedArticle[] | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showTestResults, setShowTestResults] = useState(false);

  // Set initial values when editing
  useEffect(() => {
    if (editingSource) {
      setName(editingSource.name);
      setRssUrl(editingSource.rss_url);
      setFeedType(editingSource.feed_type);
      setCategoryId(editingSource.category_id || '');
      setMaxArticles(editingSource.max_articles);
      setCurrentFallbackImageUrl(editingSource.fallback_image_url);
      setFallbackImageFile(null);
      // HTML fields
      setUseAiExtraction(editingSource.use_ai_extraction ?? true);
      setHtmlArticleSelector(editingSource.html_article_selector || '');
      setHtmlTitleSelector(editingSource.html_title_selector || '');
      setHtmlLinkSelector(editingSource.html_link_selector || '');
      setHtmlDescriptionSelector(editingSource.html_description_selector || '');
      setHtmlImageSelector(editingSource.html_image_selector || '');
      setHtmlDateSelector(editingSource.html_date_selector || '');
    } else {
      setName('');
      setRssUrl('');
      setFeedType('rss');
      setCategoryId('');
      setMaxArticles(5);
      setCurrentFallbackImageUrl(null);
      setFallbackImageFile(null);
      // Reset HTML fields
      setUseAiExtraction(true);
      setHtmlArticleSelector('');
      setHtmlTitleSelector('');
      setHtmlLinkSelector('');
      setHtmlDescriptionSelector('');
      setHtmlImageSelector('');
      setHtmlDateSelector('');
    }
    setTestResults(null);
    setShowTestResults(false);
  }, [editingSource, isOpen]);

  // Upload fallback image helper
  const uploadFallbackImage = async (file: File): Promise<string | null> => {
    if (!currentBrandId) return null;
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${currentBrandId}/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('newsfeed-source-fallbacks')
      .upload(fileName, file);

    if (error) {
      console.error('Error uploading fallback image:', error);
      toast.error('Failed to upload fallback image');
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('newsfeed-source-fallbacks')
      .getPublicUrl(data.path);

    return publicUrl;
  };

  // Test HTML extraction
  const handleTestExtraction = async () => {
    if (!rssUrl || !currentBrandId) {
      toast.error('Please enter a URL first');
      return;
    }

    setIsTesting(true);
    setTestResults(null);

    try {
      const { data, error } = await supabase.functions.invoke('newsfeed-test-html-extraction', {
        body: {
          brand_id: currentBrandId,
          url: rssUrl,
          use_ai_extraction: useAiExtraction,
          selectors: !useAiExtraction ? {
            article: htmlArticleSelector,
            title: htmlTitleSelector,
            link: htmlLinkSelector,
            description: htmlDescriptionSelector,
            image: htmlImageSelector,
            date: htmlDateSelector,
          } : null,
        },
      });

      if (error) throw error;

      if (data?.success && data?.articles) {
        setTestResults(data.articles);
        setShowTestResults(true);
        toast.success(`Found ${data.articles.length} articles`);
      } else {
        toast.error(data?.error || 'No articles found');
      }
    } catch (error: any) {
      console.error('Test extraction error:', error);
      toast.error(error.message || 'Failed to test extraction');
    } finally {
      setIsTesting(false);
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!currentBrandId) throw new Error('No brand selected');

      let fallbackImageUrl = null;
      if (fallbackImageFile) {
        fallbackImageUrl = await uploadFallbackImage(fallbackImageFile);
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('os_newsfeed_sources')
        .insert({
          brand_id: currentBrandId,
          user_id: user.id,
          name,
          rss_url: rssUrl,
          feed_type: feedType,
          category_id: categoryId || null,
          max_articles: maxArticles,
          fallback_image_url: fallbackImageUrl,
          // HTML fields
          use_ai_extraction: feedType === 'html' ? useAiExtraction : null,
          html_article_selector: feedType === 'html' && !useAiExtraction ? htmlArticleSelector || null : null,
          html_title_selector: feedType === 'html' && !useAiExtraction ? htmlTitleSelector || null : null,
          html_link_selector: feedType === 'html' && !useAiExtraction ? htmlLinkSelector || null : null,
          html_description_selector: feedType === 'html' && !useAiExtraction ? htmlDescriptionSelector || null : null,
          html_image_selector: feedType === 'html' && !useAiExtraction ? htmlImageSelector || null : null,
          html_date_selector: feedType === 'html' && !useAiExtraction ? htmlDateSelector || null : null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Source added successfully');
      queryClient.invalidateQueries({ queryKey: ['newsfeed-sources'] });
      closeSourceDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add source');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingSource) throw new Error('No source to update');

      let fallbackImageUrl = currentFallbackImageUrl;
      if (fallbackImageFile) {
        fallbackImageUrl = await uploadFallbackImage(fallbackImageFile);
      }

      const { data, error } = await supabase
        .from('os_newsfeed_sources')
        .update({
          name,
          rss_url: rssUrl,
          feed_type: feedType,
          category_id: categoryId || null,
          max_articles: maxArticles,
          fallback_image_url: fallbackImageUrl,
          // HTML fields
          use_ai_extraction: feedType === 'html' ? useAiExtraction : null,
          html_article_selector: feedType === 'html' && !useAiExtraction ? htmlArticleSelector || null : null,
          html_title_selector: feedType === 'html' && !useAiExtraction ? htmlTitleSelector || null : null,
          html_link_selector: feedType === 'html' && !useAiExtraction ? htmlLinkSelector || null : null,
          html_description_selector: feedType === 'html' && !useAiExtraction ? htmlDescriptionSelector || null : null,
          html_image_selector: feedType === 'html' && !useAiExtraction ? htmlImageSelector || null : null,
          html_date_selector: feedType === 'html' && !useAiExtraction ? htmlDateSelector || null : null,
        })
        .eq('id', editingSource.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Source updated successfully');
      queryClient.invalidateQueries({ queryKey: ['newsfeed-sources'] });
      closeSourceDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update source');
    },
  });

  const handleSubmit = () => {
    if (editingSource) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={closeSourceDialog} modal={true}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-xl">
        <DialogHeader>
          <DialogTitle>{editingSource ? 'Edit Source' : 'Add Source'}</DialogTitle>
          {!editingSource && (
            <DialogDescription>
              Add a feed source to collect news articles from
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="name">Source Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., TechCrunch, Company News"
            />
          </div>
          <div>
            <Label htmlFor="feed_type">Feed Type</Label>
            <Select
              value={feedType}
              onValueChange={(value: any) => setFeedType(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rss">RSS Feed</SelectItem>
                <SelectItem value="atom">Atom Feed</SelectItem>
                <SelectItem value="custom_txt">Custom TXT</SelectItem>
                <SelectItem value="html">HTML Page (Scraping)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="rss_url">
              {feedType === 'html' ? 'Page URL' : 'Feed URL'}
            </Label>
            <Input
              id="rss_url"
              value={rssUrl}
              onChange={(e) => setRssUrl(e.target.value)}
              placeholder={feedType === 'html' 
                ? "https://example.com/news" 
                : "https://example.com/feed.xml"
              }
            />
          </div>

          {/* HTML Scraping Configuration */}
          {feedType === 'html' && (
            <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <Label htmlFor="use_ai" className="font-medium">AI Extraction</Label>
                </div>
                <Switch
                  id="use_ai"
                  checked={useAiExtraction}
                  onCheckedChange={setUseAiExtraction}
                />
              </div>
              
              {useAiExtraction ? (
                <p className="text-sm text-muted-foreground">
                  AI will automatically detect and extract article links from the page. 
                  This works best for standard news listing pages.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4" />
                    <span>Configure CSS selectors for article extraction</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Article Container</Label>
                      <Input
                        value={htmlArticleSelector}
                        onChange={(e) => setHtmlArticleSelector(e.target.value)}
                        placeholder="article, .post, .news-item"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={htmlTitleSelector}
                        onChange={(e) => setHtmlTitleSelector(e.target.value)}
                        placeholder="h2, .title, .headline"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Link</Label>
                      <Input
                        value={htmlLinkSelector}
                        onChange={(e) => setHtmlLinkSelector(e.target.value)}
                        placeholder="a[href], .read-more"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={htmlDescriptionSelector}
                        onChange={(e) => setHtmlDescriptionSelector(e.target.value)}
                        placeholder=".excerpt, p, .summary"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Image</Label>
                      <Input
                        value={htmlImageSelector}
                        onChange={(e) => setHtmlImageSelector(e.target.value)}
                        placeholder="img, .thumbnail img"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Date</Label>
                      <Input
                        value={htmlDateSelector}
                        onChange={(e) => setHtmlDateSelector(e.target.value)}
                        placeholder="time, .date, [datetime]"
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleTestExtraction}
                disabled={isTesting || !rssUrl}
                className="w-full"
              >
                {isTesting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FlaskConical className="h-4 w-4 mr-2" />
                    Test Extraction
                  </>
                )}
              </Button>

              {/* Test Results */}
              {testResults && testResults.length > 0 && (
                <Collapsible open={showTestResults} onOpenChange={setShowTestResults}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between">
                      <span>Found {testResults.length} articles</span>
                      <span className="text-xs text-muted-foreground">
                        {showTestResults ? 'Hide' : 'Show'}
                      </span>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-2 max-h-48 overflow-y-auto space-y-2">
                      {testResults.slice(0, 5).map((article, idx) => (
                        <div key={idx} className="p-2 rounded border border-border bg-background text-xs">
                          <div className="font-medium truncate">{article.title || 'No title'}</div>
                          <div className="text-muted-foreground truncate">{article.link || 'No link'}</div>
                        </div>
                      ))}
                      {testResults.length > 5 && (
                        <div className="text-xs text-muted-foreground text-center">
                          + {testResults.length - 5} more articles
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="category">Category</Label>
            <Select
              value={categoryId}
              onValueChange={(value) => setCategoryId(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="max_articles">Max Articles</Label>
            <Input
              id="max_articles"
              type="number"
              value={maxArticles}
              onChange={(e) => setMaxArticles(parseInt(e.target.value) || 5)}
            />
          </div>
          <div>
            <Label htmlFor="fallback_image">Fallback Image (Optional)</Label>
            {currentFallbackImageUrl && (
              <div className="mb-2 space-y-2">
                <div className="text-sm text-muted-foreground">Current image:</div>
                <img 
                  src={currentFallbackImageUrl} 
                  alt="Current fallback" 
                  className="w-full h-24 object-cover rounded border"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentFallbackImageUrl(null)}
                  className="w-full"
                >
                  Remove Current Image
                </Button>
              </div>
            )}
            <Input
              id="fallback_image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFallbackImageFile(file);
              }}
            />
            <p className="text-xs text-muted-foreground mt-1">
              This image will be used when articles don't have images
            </p>
          </div>
          <Button onClick={handleSubmit} disabled={isLoading} className="w-full">
            {isLoading ? (editingSource ? "Updating..." : "Adding...") : (editingSource ? "Update Source" : "Add Source")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
